import { Button, Popover, Space, Table, message } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import PrintButton from "../UI/PrintButton/printbutton";
import DeleteModal from "../UI/ModalComponent/DeleteModal/deletemodal";
import type { ColumnsType } from "antd/es/table";
import { UseMutateAsyncFunction, useMutation } from "react-query";
import { AxiosResponse } from "axios";
import { useLocation } from "react-router";
import { deleteSale, getSaleById } from "../../api/sales";
import { useReactToPrint } from "react-to-print";
import HistoryPrinter from "../newMain/HistoryPrinter";
import { format } from "date-fns";
interface InvoicesListProps {
  paginatedSales: any;
  isSalesLoading: boolean;
  setPage: Dispatch<SetStateAction<number>>;
  setPaginatedSales: Dispatch<any>;
  SalesMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      from: string;
      to: string;
    },
    unknown
  >;
  page: number;
  search: string;
  from: string;
  to: string;
  tablekey: string;
}

interface InvoicesInterface {
  key: React.Key;
  invoices: string;
  Date: string;
  customer: string;
  amountText: string;
  isPaid: boolean;
  isApproved: boolean;
  isDelivered: boolean;
  isPosted: boolean;
}
const InvoicesList = ({
  paginatedSales,
  isSalesLoading,
  SalesMutation,
  setPaginatedSales,
  setPage,
  page,
  search,
  from,
  to,
  tablekey,
}: InvoicesListProps) => {
  const { t } = useTranslation("common");
  const { state } = useLocation();
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const [saleToPrint, setSaleToPrint] = useState<InvoicesInterface>();
  const [saleById, setSaleById] = useState<any>();

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      SalesMutation({
        limit: page * 10 + 10,
        q: search,
        from: from,
        to: to,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedSales(res.data?.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const { mutateAsync: saleByIdMutation, isLoading: isSaleByIdLoading } =
    useMutation("sale", () => getSaleById(state.id), {
      onSuccess: (res) => setSaleById([res?.data]),
    });
  const {
    mutateAsync: saleByToPrintIdMutation,
    isLoading: isSaleToPrintByIdLoading,
  } = useMutation("sale", (id: number) => getSaleById(id), {
    onSuccess: (res) => setSaleById([res?.data]),
  });

  useEffect(() => {
    state && saleByIdMutation();
  }, []);

  const deleteInvoiceMutation = useMutation("deleteSales", deleteSale, {
    onSuccess: () => {
      SalesMutation({
        limit: page * 10 + 10,
        q: search,
        from: from,
        to: to,
      }).then((res: any) => {
        setPaginatedSales(res.data?.data);
        message.success("Complete successfully");
      });
    },
  });

  const DeleteInvoice = (id: string, invoiceNo: string): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(invoiceNo);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteInvoiceMutation.mutate(Number(idToDelete));
    }
    setOpen(false);
  };
  const content = (record: any) => {
    return (
      <>
        <p style={{ fontWeight: "bold" }}>{t("status")}</p>
        <p>
          {record.isApproved ? (
            <CheckOutlined
              style={{ fontSize: "16px" }}
              className="check-mark"
            />
          ) : (
            <CloseOutlined style={{ fontSize: "16px" }} className="x-mark" />
          )}
          {t("approved")}
        </p>
        <p>
          {record.isDelivered ? (
            <CheckOutlined
              style={{ fontSize: "16px" }}
              className="check-mark"
            />
          ) : (
            <CloseOutlined style={{ fontSize: "16px" }} className="x-mark" />
          )}
          {t("delivered")}
        </p>
        <p>
          {record.isPaid ? (
            <CheckOutlined
              style={{ fontSize: "16px" }}
              className="check-mark"
            />
          ) : (
            <CloseOutlined style={{ fontSize: "16px" }} className="x-mark" />
          )}
          {t("paid")}
        </p>
        <p>
          {record.isPosted ? (
            <CheckOutlined
              style={{ fontSize: "16px" }}
              className="check-mark"
            />
          ) : (
            <CloseOutlined style={{ fontSize: "16px" }} className="x-mark" />
          )}
          {t("posted")}
        </p>
      </>
    );
  };

  const columns: ColumnsType<InvoicesInterface> = [
    {
      title: `${t("date")}`,
      dataIndex: "date",
      key: "date",
      width: "10rem",
      render: (date: any) => {
        return format(new Date(date), "MM/dd/yyyy HH:mm");
      },
    },
    {
      title: `${t("invoice")}`,
      key: "invoiceNo",
      width: "9rem",
      className: "table-header",
      render: (record) => (
        <Popover trigger="click" content={content(record)} placement="bottom">
          <Button type="text" className="Button">
            <InfoCircleOutlined
              style={{ color: "#212121", fontSize: "14px" }}
            />
            {record.invoiceNo}
          </Button>
        </Popover>
      ),
    },
    {
      title: `${t("customer")}`,
      dataIndex: "customer",
      key: "customer",
      render: (record: any) => {
        return record ? record.name : null;
      },
    },
    {
      title: `${t("amount")}`,
      dataIndex: "amountText",
      key: "amountText",
      width: "12rem",
    },
    {
      title: `${t("notes")}`,
      dataIndex: "notes",
      key: "notes",
    },
    {
      title: `${t("action")}`,
      key: "action",
      width: "7rem",
      align: "center",
      render: (record) => (
        <>
          <PrintButton
            onClick={() =>
              saleByToPrintIdMutation(record?.id).then((res) => {
                setSaleToPrint(res.data);
              })
            }
          />
          <EditButton onClick={() => console.log("hh")} />
          <DeleteButton
            onClick={() => DeleteInvoice(record.id, record.invoiceNo)}
          />
        </>
      ),
    },
  ];
  useEffect(() => {
    saleToPrint && handlePrint();
  }, [saleToPrint]);
  const componentRef = useRef<any>();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />
      <Table
        key={tablekey}
        scroll={{ x: 1000 }}
        dataSource={state ? saleById : paginatedSales}
        columns={columns}
        loading={isSalesLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record: any) => record.id}
      />
      <div style={{ display: "none" }}>
        {saleToPrint && (
          <HistoryPrinter data={saleToPrint} componentRef={componentRef} />
        )}
      </div>
    </>
  );
};

export default InvoicesList;
